/**
 * emailStore — central reactive state for the email UI.
 *
 * Built on @preact/signals. All user interactions mutate this store;
 * Preact components subscribe automatically via signal reads.
 *
 * CSR mode: all data is fetched from JSON API endpoints; no Turbo Streams
 * are used for content delivery. ActionCable (EmailJsonChannel) pushes
 * live sidebar count updates as JSON events.
 *
 * Signal groups:
 *   - Layout    : sidebar collapsed state, list width, active panel (mobile)
 *   - Data      : emails array, thread object, sidebar counts
 *   - List      : selected email ids, current thread id, loading flags
 *   - Search    : text, quick filter, sort, advanced filters
 *   - Thread    : thread loading flag, comment form visibility, audit log visibility
 *   - Compose   : open state, type, draft id, dirty flag
 *   - Sidebar   : active folder key, active mailbox id
 */

import { signal, computed, batch, effect } from '@preact/signals'
import { get, post, patch, destroy } from '@rails/request.js'
import { subscribe as cableSubscribe } from '../../lib/cable.js'
import { routeEmailFrame, EMAIL_CABLE_CHANNEL, folderKeyToInboxParams } from '@tshmanager/email-shared'
import {
  emails_inbox_path,
  emails_thread_path,
  audit_events_emails_thread_path,
  mark_opened_emails_thread_path,
  update_state_emails_thread_path,
  change_status_emails_thread_path,
  assign_responsible_emails_thread_path,
  move_to_folder_emails_thread_path,
  emails_bulk_path,
  emails_comments_path,
  emails_comment_path,
  emails_task_link_path,
  toggle_emails_tags_path,
  emails_tags_path,
} from '../../routes.js'

// ── Helpers ────────────────────────────────────────────────────────────────

async function apiGet(url, { headers } = {}) {
  const options = { responseKind: 'json' }
  if (headers) options.headers = headers
  const response = await get(url, options)
  if (!response.ok) throw new Error(`GET ${url} → ${response.statusCode}`)
  return response.json
}

async function apiPatch(url, body = {}) {
  const response = await patch(url, { body: JSON.stringify(body), responseKind: 'json' })
  if (!response.ok) throw new Error(`PATCH ${url} → ${response.statusCode}`)
  return response.json
}

async function apiPost(url, body = {}) {
  const response = await post(url, { body: JSON.stringify(body), responseKind: 'json' })
  if (!response.ok) throw new Error(`POST ${url} → ${response.statusCode}`)
  return response.json
}

// Fire-and-forget "I opened this thread" signal. Kept as a dedicated PATCH
// (never HTTP-cached) rather than relying on the GET below as a side-effect
// channel: a hover-prefetch response and the real open's GET share the same
// URL, and without disabling browser caching entirely (which would defeat
// the point of prefetching) the browser may satisfy the real open's fetch
// straight from its HTTP cache, in which case the request never reaches
// Emails::ThreadsController#show at all. This call is independent of that,
// so mark-as-read + the thread_updated broadcast always fire on a real open.
function ackThreadOpened(emailId) {
  patch(mark_opened_emails_thread_path({ id: emailId, format: 'json' }), { responseKind: 'json' })
    .catch(err => console.error('[emailStore] ackThreadOpened failed:', err))
}

async function apiDelete(url) {
  const response = await destroy(url, { responseKind: 'json' })
  if (!response.ok) throw new Error(`DELETE ${url} → ${response.statusCode}`)
  return response.statusCode === 204 ? null : response.json
}

// ── Store factory ─────────────────────────────────────────────────────────────

export function createEmailStore({ i18n } = {}) {

  // ── Layout ───────────────────────────────────────────────────────────────

  const sidebarCollapsed = signal(
    localStorage.getItem('email_sidebar_collapsed') === 'true'
  )
  const listWidth = signal(
    parseInt(localStorage.getItem('email_list_width') || '320', 10)
  )
  // Mobile: 'sidebar' | 'list' | 'pane'
  const activePanel = signal(window.innerWidth < 768 ? 'sidebar' : null)
  const isMobileLayout = signal(window.innerWidth < 768)

  // ── Data — email list ─────────────────────────────────────────────────────

  // Array of email row objects from the JSON API
  const emails = signal([])
  const emailsMeta = signal({ total_count: 0, page: 1, next_page: null, unread_count: 0 })
  const emailsLoading = signal(false)
  const emailsLoadingMore = signal(false)

  // ── Data — current thread ─────────────────────────────────────────────────

  // Full thread object from /emails/threads/:id.json
  const thread = signal(null)
  const threadLoading = signal(false)

  // ── Data — sidebar counts ─────────────────────────────────────────────────

  // shape: { system_folders: {inbox: N, ...}, mailboxes: {id: N}, custom_folders: {id: N}, tags: {id: N} }
  // Updated locally after each action; ActionCable 'unread_count' events update OTHER clients only.
  const sidebarCounts = signal({})

  // Adjust a system folder count by delta without a server round-trip.
  function _adjustCount(folderKey, delta) {
    const c = sidebarCounts.value
    const current = c?.system_folders?.[folderKey] ?? 0
    sidebarCounts.value = {
      ...c,
      system_folders: { ...(c.system_folders || {}), [folderKey]: Math.max(0, current + delta) }
    }
  }

  // Decrement the list-header unread count when viewing inbox/unread folders.
  function _adjustMetaUnreadCount(delta) {
    const folder = activeFolder.value
    if (folder !== 'inbox' && folder !== 'unread') return
    const meta = emailsMeta.value
    emailsMeta.value = {
      ...meta,
      unread_count: Math.max(0, (meta.unread_count ?? 0) + delta),
    }
  }

  // ── List & selection ─────────────────────────────────────────────────────

  const selectedIds     = signal([])
  const currentThreadId = signal(null)
  const listLoading     = signal(false)

  // ── Search & filter ──────────────────────────────────────────────────────

  const searchText      = signal('')
  const quickFilter     = signal('')
  const sortOrder       = signal('newest')
  // { responsible_id, status, ... } — advanced filter select values
  const advancedFilters = signal({})
  const filtersOpen     = signal(false)

  // ── Thread UI flags ───────────────────────────────────────────────────────

  const commentFormVisible = signal(false)
  const auditLogVisible    = signal(false)
  // Expanded/collapsed state per email id: { [emailId]: boolean }
  const expandedMessages   = signal({})
  // Audit events loaded on demand
  const auditEvents        = signal([])
  const auditEventsLoading = signal(false)

  // ── Compose ──────────────────────────────────────────────────────────────

  const composeOpen  = signal(false)
  const composeType  = signal('new')   // 'new' | 'reply' | 'reply_all' | 'forward'
  const draftId      = signal(null)
  const composeDirty = signal(false)
  // For reply/forward: the original email id
  const composeOriginalEmailId = signal(null)
  // Pre-filled draft data when resuming from a draft card (cleared after ComposeForm reads it)
  const composeDraftData = signal(null)

  // ── Sidebar ──────────────────────────────────────────────────────────────

  const activeFolder  = signal('inbox')
  const activeMailbox = signal(null)   // mailbox id or null (all)
  // Bootstrapped from data attributes on the shell div
  const mailboxes        = signal([])
  const personalMailboxes = signal([])
  const emailFolders  = signal([])
  const emailTags     = signal([])

  // ── Computed ─────────────────────────────────────────────────────────────

  const hasSelection    = computed(() => selectedIds.value.length > 0)
  const selectionCount  = computed(() => selectedIds.value.length)
  const isComposing     = computed(() => composeOpen.value)
  const hasActiveThread = computed(() => currentThreadId.value !== null)

  // ── Persistence effects ──────────────────────────────────────────────────

  effect(() => {
    localStorage.setItem('email_sidebar_collapsed', String(sidebarCollapsed.value))
  })

  effect(() => {
    localStorage.setItem('email_list_width', String(listWidth.value))
  })

  // ── Layout mutators ──────────────────────────────────────────────────────

  function toggleSidebar() {
    if (isMobileLayout.value) {
      activePanel.value = activePanel.value === 'sidebar' ? 'list' : 'sidebar'
    } else {
      sidebarCollapsed.value = !sidebarCollapsed.value
    }
  }

  function showSidebarPanel() {
    if (isMobileLayout.value) activePanel.value = 'sidebar'
  }

  function showListPanel() {
    if (isMobileLayout.value) activePanel.value = 'list'
  }

  function showReadingPane() {
    if (isMobileLayout.value) activePanel.value = 'pane'
  }

  function setListWidth(px) {
    listWidth.value = Math.max(200, Math.min(600, px))
  }

  function setMobileLayout(mobile) {
    batch(() => {
      isMobileLayout.value = mobile
      if (mobile) {
        if (activePanel.value === null) activePanel.value = 'sidebar'
      } else {
        activePanel.value = null
      }
    })
  }

  // ── JSON fetch: email list ────────────────────────────────────────────────

  /**
   * Builds the query string for the inbox JSON endpoint from current signals.
   */
  function buildInboxUrl(page = 1) {
    const url = new URL(emails_inbox_path({ format: 'json' }), window.location.origin)
    const folder = activeFolder.value
    const mailboxId = activeMailbox.value

    const folderParams = folderKeyToInboxParams(folder)
    Object.entries(folderParams).forEach(([key, val]) => {
      if (Array.isArray(val)) {
        val.forEach(v => url.searchParams.append(key, v))
      } else {
        url.searchParams.set(key, val)
      }
    })

    // inbox default — no folder param needed

    // Always send mailbox_id — empty string means "all mailboxes" so the backend
    // clears any session-stored mailbox rather than using a stale one.
    url.searchParams.set('mailbox_id', mailboxId ?? '')
    if (page > 1) url.searchParams.set('page', page)

    const text = searchText.value.trim()
    if (text) url.searchParams.set('text', text)

    const qf = quickFilter.value
    if (qf) url.searchParams.set('quick_filter', qf)

    const sort = sortOrder.value
    if (sort && sort !== 'newest') url.searchParams.set('sort', sort)

    const filters = advancedFilters.value
    Object.entries(filters).forEach(([key, val]) => {
      if (val) url.searchParams.set(key, val)
    })

    return url.toString()
  }

  async function fetchEmails(page = 1) {
    const loading = page === 1 ? emailsLoading : emailsLoadingMore
    loading.value = true
    try {
      const data = await apiGet(buildInboxUrl(page))
      batch(() => {
        if (page === 1) {
          emails.value = data.emails || []
        } else {
          emails.value = [...emails.value, ...(data.emails || [])]
        }
        emailsMeta.value = data.meta || emailsMeta.value
        loading.value = false
      })
    } catch (err) {
      console.error('[emailStore] fetchEmails error:', err)
      loading.value = false
    }
  }

  async function fetchMoreEmails() {
    const next = emailsMeta.value.next_page
    if (!next || emailsLoadingMore.value) return
    await fetchEmails(next)
  }

  // ── JSON fetch: thread ────────────────────────────────────────────────────

  const THREAD_PREFETCH_TTL_MS = 60_000
  const THREAD_PREFETCH_MAX    = 8
  const threadPrefetchCache    = new Map()
  let prefetchTimer            = null
  let prefetchTargetId         = null

  function _applyThreadData(data) {
    thread.value = data
    const emailItems = (data.items || []).filter(i => i.type === 'email')
    if (emailItems.length > 0) {
      const expanded = {}
      expanded[emailItems[0].email.id] = true
      expandedMessages.value = expanded
    }
    commentFormVisible.value = false
    auditLogVisible.value = false
    auditEvents.value = []
  }

  function _getThreadPrefetch(emailId) {
    const entry = threadPrefetchCache.get(Number(emailId))
    if (!entry) return null
    if (Date.now() - entry.at > THREAD_PREFETCH_TTL_MS) {
      threadPrefetchCache.delete(Number(emailId))
      return null
    }
    return entry.data
  }

  function _cacheThreadPrefetch(emailId, data) {
    const id = Number(emailId)
    if (threadPrefetchCache.size >= THREAD_PREFETCH_MAX) {
      const oldest = threadPrefetchCache.keys().next().value
      threadPrefetchCache.delete(oldest)
    }
    threadPrefetchCache.set(id, { data, at: Date.now() })
  }

  function cancelThreadPrefetch() {
    clearTimeout(prefetchTimer)
    prefetchTimer = null
    prefetchTargetId = null
  }

  function scheduleThreadPrefetch(emailId) {
    clearTimeout(prefetchTimer)
    const id = Number(emailId)
    if (!id || id === currentThreadId.value || _getThreadPrefetch(id)) return

    prefetchTargetId = id
    prefetchTimer = setTimeout(() => {
      prefetchTimer = null
      if (prefetchTargetId === id) prefetchThread(id)
    }, 120)
  }

  async function prefetchThread(emailId) {
    const id = Number(emailId)
    if (!id || id === currentThreadId.value || _getThreadPrefetch(id) || prefetchTargetId !== id) return

    try {
      const data = await apiGet(emails_thread_path({ id, format: 'json' }), {
        headers: { 'X-Sec-Purpose': 'prefetch' },
      })
      if (prefetchTargetId === id) _cacheThreadPrefetch(id, data)
    } catch (_) {
      // Silent — prefetch is best-effort
    }
  }

  async function fetchThread(emailId) {
    if (!emailId) return
    const id = Number(emailId)
    cancelThreadPrefetch()

    // Optimistic: if this thread was unread in inbox, mark it read locally and decrement count
    const emailRow = emails.value.find(e => e.id === id)
    const wasUnreadInInbox = emailRow
      ? (!emailRow.user_state?.read_at && !emailRow.user_state?.archived_at && !emailRow.user_state?.trashed_at)
      : false
    if (wasUnreadInInbox) {
      _adjustCount('inbox', -1)
      _adjustMetaUnreadCount(-1)
      _updateEmailRow({ ...emailRow, user_state: { ...(emailRow.user_state || {}), read_at: new Date().toISOString() } })
    }

    const cached = _getThreadPrefetch(id)
    if (cached) {
      batch(() => {
        _applyThreadData(cached)
        threadLoading.value = false
      })
    } else {
      threadLoading.value = true
    }

    // Explicit "opened" signal, independent of whether the GET below is
    // served fresh or straight from the browser's HTTP cache (a prior
    // hover-prefetch response sharing the exact same URL) — always fired so
    // the server-side mark-as-read + thread_updated broadcast never depend
    // on caching behavior.
    ackThreadOpened(id)

    try {
      const data = await apiGet(emails_thread_path({ id, format: 'json' }))
      batch(() => {
        _applyThreadData(data)
        threadLoading.value = false
      })
      threadPrefetchCache.delete(id)
    } catch (err) {
      // Revert optimistic updates if request failed
      if (wasUnreadInInbox) {
        _adjustCount('inbox', +1)
        _adjustMetaUnreadCount(+1)
        _updateEmailRow({ ...emailRow, user_state: { ...(emailRow?.user_state || {}), read_at: null } })
      }
      console.error('[emailStore] fetchThread error:', err)
      threadLoading.value = false
    }
  }

  // ── Email list mutators (select thread, optimistic update) ────────────────

  function selectThread(emailId) {
    const id = emailId ? Number(emailId) : null
    batch(() => {
      currentThreadId.value = id
      showReadingPane()
    })
    if (id) fetchThread(id)
  }

  function threadLoaded() {
    listLoading.value = false
  }

  function toggleSelected(id) {
    const numId = Number(id)
    const current = selectedIds.value
    selectedIds.value = current.includes(numId)
      ? current.filter(i => i !== numId)
      : [...current, numId]
  }

  function selectAll(ids) {
    selectedIds.value = ids.map(Number)
  }

  function clearSelection() {
    selectedIds.value = []
  }

  function setCurrentThread(emailId) {
    currentThreadId.value = emailId ? Number(emailId) : null
  }

  function clearCurrentThread() {
    batch(() => {
      currentThreadId.value = null
      thread.value = null
    })
  }

  function focusNext() {
    const list = emails.value
    const idx = list.findIndex(e => e.id === currentThreadId.value)
    if (idx < list.length - 1) selectThread(list[idx + 1].id)
  }

  function focusPrev() {
    const list = emails.value
    const idx = list.findIndex(e => e.id === currentThreadId.value)
    if (idx > 0) selectThread(list[idx - 1].id)
  }

  // Optimistically update a single email row in the emails list by id
  function _updateEmailRow(updatedEmail) {
    emails.value = emails.value.map(e => e.id === updatedEmail.id ? { ...e, ...updatedEmail } : e)
  }

  function _removeEmailRow(emailId) {
    emails.value = emails.value.filter(e => e.id !== emailId)
  }

  // ── Search mutators ──────────────────────────────────────────────────────

  function setSearchText(text) {
    searchText.value = text
  }

  function setQuickFilter(filter) {
    quickFilter.value = filter
  }

  function toggleQuickFilter(chip) {
    quickFilter.value = quickFilter.value === chip ? '' : chip
  }

  function setSort(sort) {
    sortOrder.value = sort
  }

  function setAdvancedFilter(key, value) {
    advancedFilters.value = { ...advancedFilters.value, [key]: value }
  }

  function clearAllFilters() {
    batch(() => {
      searchText.value = ''
      quickFilter.value = ''
      sortOrder.value = 'newest'
      advancedFilters.value = {}
    })
  }

  function toggleFiltersPanel() {
    filtersOpen.value = !filtersOpen.value
  }

  // Legacy stub kept for keyboard handler compatibility; CSR no longer uses Turbo Frames
  function reloadList() {
    fetchEmails(1)
  }

  function buildListUrl() {
    return buildInboxUrl()
  }

  // ── Thread UI mutators ────────────────────────────────────────────────────

  function setThreadLoading(loading) {
    threadLoading.value = loading
  }

  function showCommentForm() {
    commentFormVisible.value = true
  }

  function hideCommentForm() {
    commentFormVisible.value = false
  }

  function toggleAuditLog(visible) {
    auditLogVisible.value = visible
    if (visible && thread.value && auditEvents.value.length === 0) {
      loadAuditEvents()
    }
  }

  function toggleMessageExpanded(emailId) {
    const id = Number(emailId)
    expandedMessages.value = {
      ...expandedMessages.value,
      [id]: !expandedMessages.value[id],
    }
  }

  function expandMessage(emailId) {
    expandedMessages.value = { ...expandedMessages.value, [Number(emailId)]: true }
  }

  function collapseMessage(emailId) {
    expandedMessages.value = { ...expandedMessages.value, [Number(emailId)]: false }
  }

  async function loadAuditEvents() {
    if (!currentThreadId.value) return
    auditEventsLoading.value = true
    try {
      const data = await apiGet(
        audit_events_emails_thread_path({ id: currentThreadId.value, format: 'json' })
      )
      auditEvents.value = data.events || []
    } catch (err) {
      console.error('[emailStore] loadAuditEvents error:', err)
    } finally {
      auditEventsLoading.value = false
    }
  }

  // ── Mutations (PATCH/POST with optimistic update) ─────────────────────────

  async function updateEmailState(emailId, actionType) {
    // Determine the email's current state before the request for accurate delta.
    const emailRow = emails.value.find(e => e.id === emailId)
    const userState = emailRow?.user_state || thread.value?.items?.find(i => i.type === 'email')?.email?.user_state || {}
    const isInbox    = !userState.archived_at && !userState.trashed_at
    const isArchived = !!userState.archived_at
    const isTrashed  = !!userState.trashed_at
    const isUnread   = !userState.read_at
    const isStarred  = !!(userState.starred_at || userState.starred)

    // Optimistic update for star toggle — immediately reflect in UI before server responds
    if (actionType === 'star' && emailRow) {
      const optimisticState = {
        ...emailRow.user_state,
        starred: !isStarred,
        starred_at: !isStarred ? new Date().toISOString() : null,
      }
      _updateEmailRow({ ...emailRow, user_state: optimisticState })
    }

    try {
      const data = await apiPatch(
        update_state_emails_thread_path({ id: emailId, format: 'json' }),
        { action_type: actionType }
      )

      // ── Instant local signal updates (no ActionCable round-trip needed) ──
      switch (actionType) {
        case 'archive':
          if (isInbox && isUnread)  _adjustCount('inbox',    -1)
          _adjustCount('archived', +1)
          break
        case 'trash':
          if (isInbox && isUnread)  _adjustCount('inbox',    -1)
          _adjustCount('trash',    +1)
          break
        case 'restore':
          if (isArchived) _adjustCount('archived', -1)
          if (isTrashed)  _adjustCount('trash',    -1)
          break
        case 'star':
          _adjustCount('starred', isStarred ? -1 : +1)
          break
        case 'unread':
          if (!isUnread) {
            _adjustCount('inbox', isInbox ? +1 : 0)
            if (isInbox) _adjustMetaUnreadCount(+1)
          }
          break
        case 'read':
          if (isUnread && isInbox) {
            _adjustCount('inbox', -1)
            _adjustMetaUnreadCount(-1)
          }
          break
      }

      if (data.removed) {
        _removeEmailRow(emailId)
        if (currentThreadId.value === emailId) clearCurrentThread()
      } else if (data.email) {
        _updateEmailRow(data.email)
        if (thread.value?.id === emailId) {
          thread.value = { ...thread.value, ...data.email }
        }
      }
    } catch (err) {
      // Revert optimistic star update on server error
      if (actionType === 'star' && emailRow) {
        _updateEmailRow(emailRow)
      }
      console.error('[emailStore] updateEmailState error:', err)
    }
  }

  async function changeEmailStatus(emailId, status) {
    try {
      const data = await apiPatch(
        change_status_emails_thread_path({ id: emailId, format: 'json' }),
        { status }
      )
      if (data.email) _updateEmailRow(data.email)
      if (thread.value?.id === emailId) {
        thread.value = { ...thread.value, status }
      }
    } catch (err) {
      console.error('[emailStore] changeEmailStatus error:', err)
    }
  }

  async function assignResponsible(emailId, responsibleId) {
    try {
      await apiPatch(
        assign_responsible_emails_thread_path({ id: emailId, format: 'json' }),
        { responsible_id: responsibleId || '' }
      )
      if (thread.value?.id === emailId) {
        // Re-fetch thread to get updated responsible
        fetchThread(emailId)
      }
    } catch (err) {
      console.error('[emailStore] assignResponsible error:', err)
    }
  }

  async function moveToFolder(emailId, folderId) {
    const emailRow = emails.value.find(e => e.id === emailId)
    const userState = emailRow?.user_state || {}
    const isInbox = !userState.archived_at && !userState.trashed_at
    const isUnread = !userState.read_at

    try {
      await apiPatch(
        move_to_folder_emails_thread_path({ id: emailId, format: 'json' }),
        { email_folder_id: folderId || '' }
      )
      // Custom folder count +1 for target; inbox unread -1 if moving out of inbox unread
      if (isInbox && isUnread) {
        _adjustCount('inbox', -1)
        _adjustMetaUnreadCount(-1)
      }
      if (folderId) {
        const c = sidebarCounts.value
        sidebarCounts.value = {
          ...c,
          custom_folders: { ...(c.custom_folders || {}), [folderId]: Math.max(0, (c.custom_folders?.[folderId] ?? 0) + (isUnread ? 1 : 0)) }
        }
      }
      _removeEmailRow(emailId)
      if (currentThreadId.value === emailId) clearCurrentThread()
    } catch (err) {
      console.error('[emailStore] moveToFolder error:', err)
    }
  }

  async function toggleTag(emailId, tagId) {
    const appliedBefore = (thread.value?.email_tag_ids || []).includes(tagId)
    try {
      const data = await apiPost(
        toggle_emails_tags_path({ format: 'json' }),
        { email_id: emailId, tag_id: tagId }
      )
      if (thread.value?.id === emailId && data.email_tag_ids != null) {
        thread.value = { ...thread.value, email_tag_ids: data.email_tag_ids }
      }
      // Adjust tag count locally
      const c = sidebarCounts.value
      const delta = appliedBefore ? -1 : +1
      sidebarCounts.value = {
        ...c,
        tags: { ...(c.tags || {}), [tagId]: Math.max(0, (c.tags?.[tagId] ?? 0) + delta) }
      }
    } catch (err) {
      console.error('[emailStore] toggleTag error:', err)
    }
  }

  async function createTag(tagName, tagColor) {
    try {
      const data = await apiPost(
        emails_tags_path({ format: 'json' }),
        { email_tag: { tag: tagName, color: tagColor } }
      )
      if (data.id) {
        emailTags.value = [...emailTags.value, { id: data.id, tag: data.tag, color: data.color }]
      }
      return data
    } catch (err) {
      console.error('[emailStore] createTag error:', err)
      return null
    }
  }

  async function bulkUpdate(ids, bulkAction, extraParams = {}) {
    // Snapshot current states for accurate deltas
    const affectedEmails = emails.value.filter(e => ids.includes(e.id))
    const unreadInInboxCount = affectedEmails.filter(e => {
      const s = e.user_state || {}
      return !s.read_at && !s.archived_at && !s.trashed_at
    }).length
    const starredCount = affectedEmails.filter(e => e.user_state?.starred_at).length

    try {
      const data = await apiPatch(emails_bulk_path({ format: 'json' }), {
        ids,
        bulk_action: bulkAction,
        ...extraParams,
      })

      // ── Instant local signal updates ──
      switch (bulkAction) {
        case 'archive':
        case 'trash':
          if (unreadInInboxCount > 0) _adjustCount('inbox', -unreadInInboxCount)
          if (bulkAction === 'archive') _adjustCount('archived', +unreadInInboxCount)
          if (bulkAction === 'trash')   _adjustCount('trash',    +unreadInInboxCount)
          break
        case 'unread':
          _adjustCount('inbox', +ids.length)
          _adjustMetaUnreadCount(+ids.length)
          break
        case 'read':
          if (unreadInInboxCount > 0) {
            _adjustCount('inbox', -unreadInInboxCount)
            _adjustMetaUnreadCount(-unreadInInboxCount)
          }
          break
        case 'star':
          _adjustCount('starred', +ids.length)
          break
        case 'unstar':
          _adjustCount('starred', -starredCount)
          break
      }

      const removeFromView = data.removed_from_view
      if (removeFromView) {
        ids.forEach(id => _removeEmailRow(id))
        if (currentThreadId.value && ids.includes(currentThreadId.value)) {
          clearCurrentThread()
        }
      } else if (data.emails) {
        data.emails.forEach(e => _updateEmailRow(e))
      }
      clearSelection()
    } catch (err) {
      console.error('[emailStore] bulkUpdate error:', err)
    }
  }

  async function postComment(emailId, body) {
    try {
      const data = await apiPost(emails_comments_path({ format: 'json' }), {
        email_comment: { email_id: emailId, body }
      })
      if (data.comment && thread.value) {
        thread.value = {
          ...thread.value,
          items: [...thread.value.items, {
            type: 'comment',
            at: data.comment.created_at,
            email: null,
            comment: data.comment,
          }].sort((a, b) => new Date(b.at) - new Date(a.at)),
        }
      }
      hideCommentForm()
    } catch (err) {
      console.error('[emailStore] postComment error:', err)
    }
  }

  async function deleteComment(commentId) {
    try {
      await apiDelete(emails_comment_path({ id: commentId, format: 'json' }))
      if (thread.value) {
        thread.value = {
          ...thread.value,
          items: thread.value.items.filter(
            i => !(i.type === 'comment' && i.comment?.id === commentId)
          ),
        }
      }
    } catch (err) {
      console.error('[emailStore] deleteComment error:', err)
    }
  }

  async function unlinkTask(connectionId) {
    try {
      await apiDelete(emails_task_link_path({ id: connectionId, format: 'json' }))
      if (thread.value) {
        thread.value = {
          ...thread.value,
          infobar: {
            ...thread.value.infobar,
            tasks: (thread.value.infobar?.tasks || []).filter(
              t => t.connection_id !== connectionId
            ),
          },
        }
      }
    } catch (err) {
      console.error('[emailStore] unlinkTask error:', err)
    }
  }

  // ── Compose mutators ─────────────────────────────────────────────────────

  function openCompose(type = 'new', originalEmailId = null) {
    batch(() => {
      composeOpen.value = true
      composeType.value = type
      draftId.value = null
      composeDirty.value = false
      composeDraftData.value = null
      composeOriginalEmailId.value = originalEmailId ? Number(originalEmailId) : null
    })
  }

  function openComposeFromDraft(draft) {
    batch(() => {
      composeOpen.value = true
      composeType.value = draft.draft_type || 'new'
      draftId.value = draft.id
      composeDirty.value = false
      composeDraftData.value = draft
      composeOriginalEmailId.value = draft.reply_to_email_id ? Number(draft.reply_to_email_id) : null
    })
  }

  function closeCompose() {
    batch(() => {
      composeOpen.value = false
      draftId.value = null
      composeDirty.value = false
      composeDraftData.value = null
      composeOriginalEmailId.value = null
    })
  }

  function setDraftId(id) {
    draftId.value = id ? Number(id) : null
  }

  function markComposeDirty() {
    composeDirty.value = true
  }

  function markComposeClean() {
    composeDirty.value = false
  }

  // ── Sidebar mutators ─────────────────────────────────────────────────────

  function setActiveFolder(folder) {
    batch(() => {
      activeFolder.value = folder
      currentThreadId.value = null
      thread.value = null
      selectedIds.value = []
    })
    fetchEmails(1)
    if (isMobileLayout.value) activePanel.value = 'list'
  }

  function setActiveMailbox(mailboxId) {
    batch(() => {
      activeMailbox.value = mailboxId ? Number(mailboxId) : null
      currentThreadId.value = null
      thread.value = null
      selectedIds.value = []
    })
    fetchEmails(1)
    fetchSidebarCounts(mailboxId)
    if (isMobileLayout.value) activePanel.value = 'list'
  }

  // ── ActionCable subscription ──────────────────────────────────────────────

  let _subscription = null

  function connectActionCable(personId) {
    if (_subscription) return // Already connected
    _subscription = cableSubscribe(
      { channel: EMAIL_CABLE_CHANNEL, person_id: personId },
      {
        received(data) {
          _handleCableEvent(data)
        },
        connected() {
          // noop
        },
        disconnected() {
          // noop
        },
      }
    )
  }

  function disconnectActionCable() {
    _subscription?.unsubscribe()
    _subscription = null
  }

  const _SYSTEM_FOLDER_KEYS = new Set(['inbox', 'starred', 'archived', 'trash', 'sent', 'drafts'])

  function _normalizeSidebarCounts(counts) {
    if (!counts || typeof counts !== 'object') return {}
    if ('system_folders' in counts) return counts
    const system_folders = {}, custom_folders = {}, tags = {}, mailboxes = {}
    for (const [key, value] of Object.entries(counts)) {
      if (_SYSTEM_FOLDER_KEYS.has(key)) {
        system_folders[key] = value
      } else if (key.startsWith('folder_')) {
        const id = parseInt(key.slice(7), 10)
        if (!isNaN(id)) custom_folders[id] = value
      } else if (key.startsWith('tag_')) {
        const id = parseInt(key.slice(4), 10)
        if (!isNaN(id)) tags[id] = value
      } else if (key.startsWith('mailbox_')) {
        const id = parseInt(key.slice(8), 10)
        if (!isNaN(id)) mailboxes[id] = value
      }
    }
    return { system_folders, custom_folders, tags, mailboxes }
  }

  function _handleCableEvent(data) {
    routeEmailFrame(data, {
      onUnreadCount:    (counts) => {
        sidebarCounts.value = _normalizeSidebarCounts(counts)
        const folder = activeFolder.value
        if (folder === 'inbox' || folder === 'unread') {
          const normalized = _normalizeSidebarCounts(counts)
          const nextUnread = normalized.system_folders?.[folder] ?? normalized.system_folders?.inbox
          if (nextUnread != null) {
            emailsMeta.value = { ...emailsMeta.value, unread_count: nextUnread }
          }
        }
      },
      onEmailRowUpdated:(thread) => { if (thread) _updateEmailRow(thread) },
      onEmailRemoved:   (id)     => { _removeEmailRow(id) },
      onThreadUpdated:  (thread) => { if (thread) _applyThreadUpdated(thread) },
      onThreadCreated:  ()       => { fetchEmails(1); fetchSidebarCounts() },
      onThreadRemoved:  (id)     => { _removeEmailRow(id) },
    })
  }

  // Apply a thread_updated broadcast: patch the email list row and, if the
  // thread is currently open in the detail pane, patch thread.value too.
  function _applyThreadUpdated(updated) {
    // Update the row in the email list (status, tags, responsible_person, etc.)
    _updateEmailRow(updated)

    // If this thread is currently open, merge the updated fields into thread.value
    // so the detail view (tags, status bar, etc.) refreshes without a full reload.
    if (thread.value && thread.value.id === updated.id) {
      // The cable payload uses `tags` (full objects). Derive email_tag_ids so that
      // TagPickerDropdown checkboxes (which read email_tag_ids) stay in sync too.
      const patch = { ...updated }
      if (Array.isArray(updated.tags)) {
        patch.email_tag_ids = updated.tags.map(t => t.id)
      }
      thread.value = { ...thread.value, ...patch }
    }
  }

  async function openInitialRouteFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const threadId = params.get('thread_id')
    const mailboxParam = params.has('mailbox_id') ? params.get('mailbox_id') : null
    const folder = params.get('folder')

    batch(() => {
      if (folder) activeFolder.value = folder
      if (mailboxParam !== null) {
        activeMailbox.value = mailboxParam === '' ? null : Number(mailboxParam)
      }
    })

    await fetchEmails(1)
    if (threadId) selectThread(Number(threadId))
    if (mailboxParam !== null) fetchSidebarCounts(mailboxParam === '' ? null : Number(mailboxParam))
  }

  // ── Bootstrap from data attributes on the shell div ──────────────────────

  function bootstrap({ mailboxesData, personalMailboxesData, emailFoldersData, emailTagsData, sidebarCountsData, initialFolder }) {
    batch(() => {
      if (Array.isArray(mailboxesData))         mailboxes.value         = mailboxesData
      if (Array.isArray(personalMailboxesData)) personalMailboxes.value = personalMailboxesData
      if (Array.isArray(emailFoldersData))      emailFolders.value      = emailFoldersData
      if (Array.isArray(emailTagsData))         emailTags.value         = emailTagsData
      if (sidebarCountsData && typeof sidebarCountsData === 'object') sidebarCounts.value = sidebarCountsData
      if (initialFolder)                        activeFolder.value      = initialFolder
    })
  }

  // ── Sidebar counts (pull) ─────────────────────────────────────────────────

  async function fetchSidebarCounts(mailboxId = null) {
    try {
      const url = new URL('/emails/sidebar_counts', window.location.origin)
      // Always send mailbox_id — empty string means "all mailboxes" so the backend
      // clears any session-stored mailbox (same logic as buildInboxUrl).
      url.searchParams.set('mailbox_id', mailboxId ?? '')
      const data = await apiGet(url.toString())
      if (data?.counts) sidebarCounts.value = data.counts
    } catch (_) {
      // Silent — non-critical
    }
  }

  // ── Store object ─────────────────────────────────────────────────────────

  const store = {
    // Signals (components read .value)
    sidebarCollapsed,
    listWidth,
    activePanel,
    isMobileLayout,

    // Data signals
    emails,
    emailsMeta,
    emailsLoading,
    emailsLoadingMore,
    thread,
    threadLoading,
    sidebarCounts,

    selectedIds,
    currentThreadId,
    listLoading,
    searchText,
    quickFilter,
    sortOrder,
    advancedFilters,
    filtersOpen,
    commentFormVisible,
    auditLogVisible,
    expandedMessages,
    auditEvents,
    auditEventsLoading,
    composeOpen,
    composeType,
    draftId,
    composeDirty,
    composeOriginalEmailId,
    composeDraftData,
    activeFolder,
    activeMailbox,
    mailboxes,
    personalMailboxes,
    emailFolders,
    emailTags,

    // Computed
    hasSelection,
    selectionCount,
    isComposing,
    hasActiveThread,

    // i18n reference
    i18n,

    // Layout
    toggleSidebar,
    showSidebarPanel,
    showListPanel,
    showReadingPane,
    setListWidth,
    setMobileLayout,

    // Fetch
    fetchEmails,
    fetchMoreEmails,
    fetchThread,
    scheduleThreadPrefetch,
    cancelThreadPrefetch,
    openInitialRouteFromUrl,
    loadAuditEvents,

    // List & selection
    selectThread,
    threadLoaded,
    toggleSelected,
    selectAll,
    clearSelection,
    setCurrentThread,
    clearCurrentThread,
    focusNext,
    focusPrev,

    // Search
    setSearchText,
    setQuickFilter,
    toggleQuickFilter,
    setSort,
    setAdvancedFilter,
    clearAllFilters,
    toggleFiltersPanel,
    reloadList,
    buildListUrl,

    // Thread UI
    setThreadLoading,
    showCommentForm,
    hideCommentForm,
    toggleAuditLog,
    toggleMessageExpanded,
    expandMessage,
    collapseMessage,

    // Mutations
    updateEmailState,
    changeEmailStatus,
    assignResponsible,
    moveToFolder,
    toggleTag,
    createTag,
    bulkUpdate,
    postComment,
    deleteComment,
    unlinkTask,

    // Compose
    openCompose,
    openComposeFromDraft,
    closeCompose,
    setDraftId,
    markComposeDirty,
    markComposeClean,

    // Sidebar
    setActiveFolder,
    setActiveMailbox,

    // ActionCable
    connectActionCable,
    disconnectActionCable,
    // Bootstrap
    bootstrap,
    // Counts pull
    fetchSidebarCounts,
    adjustSidebarCount: _adjustCount,
  }

  return store
}
